// src/components/features/ResourcesLinks.js
import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../contexts/AppProvider';
import { 
    FileText, 
    ExternalLink, 
    Copy, 
    Check, 
    Eye, 
    EyeOff, 
    Lock, 
    RefreshCw, 
    AlertCircle,
    Link as LinkIcon 
} from 'lucide-react';
import API_CONFIG from '../../config/api';

const BASE_URL = API_CONFIG.BASE_URL;

function CopyButton({ text }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <button
            onClick={handleCopy}
            className="p-1.5 rounded transition-colors"
            style={{ 
                backgroundColor: copied ? 'color-mix(in srgb, var(--color-primary) 10%, transparent)' : 'transparent',
                color: copied ? 'var(--color-primary)' : 'var(--color-muted)'
            }}
            title="Copy to clipboard"
        >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </button>
    );
}

function CredentialItem({ label, value }) {
    const [isVisible, setIsVisible] = useState(false);

    return (
        <div 
            className="flex items-center justify-between p-3 rounded-md mb-2 text-sm"
            style={{ 
                backgroundColor: 'var(--color-bg)',
                border: '1px solid color-mix(in srgb, var(--color-muted) 20%, transparent)'
            }}
        >
            <div className="flex flex-col overflow-hidden mr-4">
                <span className="text-xs font-semibold uppercase mb-1" style={{ color: 'var(--color-muted)' }}>
                    {label}
                </span>
                <span className="font-mono truncate" style={{ color: 'var(--color-text)' }}>
                    {isVisible ? value : '••••••••••••••••••••'}
                </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
                <button
                    onClick={() => setIsVisible(!isVisible)}
                    className="p-1.5 rounded transition-colors hover:bg-black/5 dark:hover:bg-white/5"
                    style={{ color: 'var(--color-muted)' }}
                >
                    {isVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <CopyButton text={value} />
            </div>
        </div>
    );
}

function ResourceCard({ resource, t }) {
    const hasCredentials = resource.credentials && Object.keys(resource.credentials).length > 0;

    return (
        <div 
            className="mb-4 p-6 border shadow-sm rounded-lg transition-all duration-200 hover:shadow-md"
            style={{ 
                backgroundColor: 'var(--color-surface)',
                borderColor: 'color-mix(in srgb, var(--color-muted) 50%, transparent)'
            }}
        >
            <div className="flex flex-col md:flex-row gap-6">
                {/* Icon Column */}
                <div className="flex-shrink-0">
                    <div 
                        className="h-12 w-12 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: 'color-mix(in srgb, var(--color-primary) 10%, transparent)' }}
                    >
                        <FileText className="h-6 w-6" style={{ color: 'var(--color-primary)' }} />
                    </div>
                </div>

                {/* Content Column */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <h3 className="text-lg font-bold mb-1" style={{ color: 'var(--color-text)' }}>
                                {resource.label}
                            </h3>
                            <p className="text-sm mb-4" style={{ color: 'var(--color-muted)' }}>
                                {resource.description}
                            </p>
                        </div>
                        {resource.url && (
                            <a 
                                href={resource.url}
                                target={resource.open_in === 'new_tab' ? '_blank' : '_self'}
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                                style={{ 
                                    backgroundColor: 'var(--color-primary)', 
                                    color: '#ffffff' // Assuming white text on primary
                                }}
                            >
                                <span>{t('resourceOpenLink')}</span>
                                <ExternalLink className="h-4 w-4" />
                            </a>
                        )}
                    </div>

                    {/* URL Display */}
                    {resource.url && (
                        <div className="mb-4">
                            <div className="flex items-center gap-2 mb-1">
                                <LinkIcon className="h-3 w-3" style={{ color: 'var(--color-muted)' }} />
                                <span className="text-xs font-semibold uppercase" style={{ color: 'var(--color-muted)' }}>
                                    {t('resourceUrl')}
                                </span>
                            </div>
                            <div 
                                className="flex items-center justify-between p-3 rounded-md text-sm"
                                style={{ 
                                    backgroundColor: 'var(--color-bg)',
                                    border: '1px solid color-mix(in srgb, var(--color-muted) 20%, transparent)'
                                }}
                            >
                                <span className="truncate mr-4 text-blue-500 hover:underline cursor-pointer" onClick={() => window.open(resource.url, '_blank')}>
                                    {resource.url}
                                </span>
                                <CopyButton text={resource.url} />
                            </div>
                        </div>
                    )}

                    {/* Credentials Display */}
                    {hasCredentials && (
                        <div className="mt-4 pt-4" style={{ borderTop: '1px solid color-mix(in srgb, var(--color-muted) 20%, transparent)' }}>
                            <div className="flex items-center gap-2 mb-3">
                                <Lock className="h-3 w-3" style={{ color: 'var(--color-muted)' }} />
                                <span className="text-xs font-semibold uppercase" style={{ color: 'var(--color-muted)' }}>
                                    {t('resourceCredentials')}
                                </span>
                            </div>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {Object.entries(resource.credentials).map(([key, value]) => (
                                    <CredentialItem key={key} label={key.replace(/_/g, ' ')} value={value} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function ResourcesLinks({ featureName }) {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const { tenantInfos, t } = useApp();

    const fetchFeatureData = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const tenant_id = tenantInfos.interfaceId;
            const access_token = tenantInfos.accessToken;
            // Using the pattern defined in QuotasTrackingFeature
            const api_url = `${BASE_URL}/cui_portal/${tenant_id}/${access_token}/features/get_data/${featureName}`;
            
            const result = await fetch(api_url);
            
            if (result.status === 404) {
                setError({ statusCode: 404, detail: "Feature data not found" });
                return;
            }
            if (result.status === 401) {
                setError({ statusCode: 401, detail: t("error401") });
                return;
            }
            if (!result.ok) {
                setError({ statusCode: 500, detail: t("error500") });
                return;
            }
            
            const response = await result.json();
            
            if (response.success && response.data) {
                setData(response.data);
                setError(null);
            } else if (response.error) {
                setError({ statusCode: response.error.status_code, detail: response.error.detail || response.error.message });
            } else {
                setError({ statusCode: 422, detail: t("error422") });
            }
        } catch (err) {
            console.error("Error fetching feature data:", err);
            setError({ statusCode: 500, detail: t("error500") });
        } finally {
            setIsLoading(false);
        }
    }, [featureName, tenantInfos, t]);

    useEffect(() => {
        fetchFeatureData();
    }, [fetchFeatureData]);

    const handleRefreshButton = () => {
        fetchFeatureData();
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="max-w-7xl mx-auto px-6 py-12">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-center">
                        <div 
                            className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
                            style={{ borderColor: 'var(--color-primary)' }}
                        />
                        <p style={{ color: 'var(--color-muted)' }}>{t('loading')}</p>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="max-w-7xl mx-auto px-6 py-12">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div 
                        className="p-6 border rounded-lg max-w-lg w-full"
                        style={{ 
                            backgroundColor: 'color-mix(in srgb, red 5%, var(--color-surface))',
                            borderColor: 'color-mix(in srgb, red 30%, transparent)'
                        }}
                    >
                        <div className="flex items-start gap-3">
                            <AlertCircle className="h-6 w-6 flex-shrink-0" style={{ color: 'red' }} />
                            <div>
                                <h3 className="font-semibold mb-1" style={{ color: 'var(--color-text)' }}>
                                    Error {error.statusCode}
                                </h3>
                                <p style={{ color: 'var(--color-text)', whiteSpace: 'pre-line' }}>
                                    {error.detail}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const resources = data?.resources || [];

    return (
        <div className="mx-auto px-6 py-8 max-w-5xl">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-end gap-3">
                    <FileText className="h-8 w-8 mb-1" style={{ color: 'var(--color-primary)' }} />
                    <h1 className="text-3xl font-bold" style={{ color: 'var(--color-primary)' }}>
                        {t("resourcesTitle")}
                    </h1>
                </div>
                <button
                    onClick={handleRefreshButton}
                    className="px-4 py-2 border rounded-md transition-all duration-200 hover:shadow-md"
                    style={{
                        backgroundColor: 'var(--color-surface)',
                        borderColor: 'color-mix(in srgb, var(--color-muted) 50%, transparent)',
                        color: 'var(--color-text)'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--color-primary) 5%, var(--color-surface))';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                    }}
                >
                    <div className="flex items-center gap-2">
                        <RefreshCw className="h-4 w-4" />
                        <span className="text-sm font-medium">{t("refresh")}</span>
                    </div>
                </button>
            </div>

            {/* List */}
            {resources.length > 0 ? (
                <div className="space-y-4">
                    {resources.map((resource) => (
                        <ResourceCard 
                            key={resource.id || resource.url} 
                            resource={resource}
                            t={t}
                        />
                    ))}
                </div>
            ) : (
                <div 
                    className="p-12 border rounded-lg text-center"
                    style={{ 
                        backgroundColor: 'var(--color-surface)',
                        borderColor: 'color-mix(in srgb, var(--color-muted) 50%, transparent)'
                    }}
                >
                    <div className="flex flex-col items-center">
                        <FileText className="h-12 w-12 mb-4 opacity-20" style={{ color: 'var(--color-text)' }} />
                        <p style={{ color: 'var(--color-muted)' }}>{t("noResources")}</p>
                    </div>
                </div>
            )}
        </div>
    );
}